import { useEffect, useState } from "react";
import DataTable from "../../../utils/components/DataTable";
import { GrAttachment } from "react-icons/gr";
import { useUser } from "../../../utils/hooks/useUser";
import { format } from "date-fns";
import { uniqueId } from "lodash";
import { FaTrash } from "react-icons/fa";
import { Translate } from "react-localize-redux";
import { useLocation, useParams } from "react-router";
import API from "../../../utils/API";
import { useProject } from "../../../utils/hooks/useProject";
import toBase64 from "../../../utils/helpers/toBase64";
import CustomDropzone from "../../../utils/components/CustomDropzone";

const Attachments = ({
  title,
  data,
  handleUpload,
  disabled,
  isLock,
  setIsUpdated,
}) => {
  const [files, setFiles] = useState([]);
  const user = useUser();
  const [columns, setColumns] = useState([]);
  const { id } = useParams();
  const { project } = useProject();
  const { search } = useLocation();

  useEffect(() => {
    if (data && JSON.stringify(data) !== JSON.stringify(files)) {
      setFiles(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    const actionsIsLocked = new URLSearchParams(search).get("locked");

    const columns = [
      {
        name: (
          <span className="text-uppercase">
            <Translate id="forms.attachments.file_name" />
          </span>
        ),
        selector: (row) => row.name,
        sortable: true,
        left: true,
        cell: (row) => (
          <div className="actions-holder d-flex align-items-center justify-content-between">
            <div
              role="button"
              className="action-item"
              onClick={() => handleDownloadFile(row.id)}
            >
              <GrAttachment className="fs-4 me-2" />
              {row.name}
            </div>
          </div>
        ),
      },
      {
        name: (
          <span className="text-uppercase">
            <Translate id="forms.attachments.uploaded_by" />
          </span>
        ),
        selector: (row) => row.users,
        sortable: true,
        center: true,
        cell: (row) => row.uploadedBy,
      },
      {
        name: (
          <span className="text-uppercase">
            <Translate id="forms.attachments.uploaded_at" />
          </span>
        ),
        selector: (row) => row.id,
        center: true,
        cell: (row) => row.uploadedAt,
      },
      {
        name: (
          <span className="text-uppercase">
            <Translate id="actions" />
          </span>
        ),
        center: true,
        cell: (row) => (
          <div className="actions-holder d-flex align-items-center justify-content-between">
            <div
              role="button"
              className="action-item"
              onClick={() => handleRemoveFile(row.id)}
            >
              <FaTrash className="fs-6 text-danger" title="Sil" />
            </div>
          </div>
        ),
        omit: disabled || isLock || actionsIsLocked,
      },
    ];

    setColumns(columns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, disabled, isLock]);

  const handleRemoveFile = (fileId) => {
    const newFiles = files.filter((file) => file.id !== fileId);
    if (
      typeof fileId === "string" ||
      String(fileId)?.startsWith("attachment_")
    ) {
      setFiles(newFiles);
      handleUpload && handleUpload(newFiles);
      return;
    }

    if (id) {
      API.putData(`form_attachment/${id}?project_id=${project.id}`, {
        deleted_ids: [fileId],
        prev_form_id: id,
      }).then(() => {
        setFiles(newFiles);
        handleUpload && handleUpload(newFiles);
      });
    }
  };

  const handleDownloadFile = (id) => {
    const file = files.find((file) => file.id === id);
    const link = document.createElement("a");
    link.href = file.url;
    link.download = file.name;
    link.click();
  };

  return (
    <div className="row ps-5 py-3 mt-4 border-top border-2">
      <div className="col-12">
        <p className="fs-5 text-orange my-3">{title}</p>
      </div>
      <div className="col-11 col-lg-2 border--dashed border-secondary border-3 rounded cursor--pointer my-4 ms-2 me-5">
        <div className="mt-3 text-center">
          <CustomDropzone
            options={{
              accept: "",
              onDrop: async (acceptedFiles) => {
                const newFiles = await Promise.all(
                  acceptedFiles.map(async (file) => {
                    const base64 = await toBase64(file);
                    return {
                      url: URL.createObjectURL(file),
                      base64,
                      name: file.name,
                      uploadedBy: user?.name,
                      uploadedAt: format(new Date(), "dd.MM.yyyy HH:mm"),
                      id: uniqueId("attachment_"),
                    };
                  })
                );

                const updatedFiles = [...files, ...newFiles];
                setFiles(updatedFiles);
                handleUpload && handleUpload(updatedFiles);

                if (setIsUpdated) setIsUpdated(true);
              },
              disabled: disabled || isLock,
            }}
            label={<Translate id="forms.browse-files-to-upload-image" />}
            className={`dropzone ${
              disabled || isLock ? "cursor--disabled disabled" : ""
            }`}
            icon={<GrAttachment className="fs-1 my-4" />}
          />
        </div>
      </div>
      <div className="col-12 col-lg-7 ms-0 ms-lg-5">
        <DataTable columns={columns} data={files} />
      </div>
    </div>
  );
};
export default Attachments;
